// Firmalar ekranı: self-servis kayıt başvurusu inceleme (Onayla / Reddet)
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { CheckCircle2, XCircle, Eye, ShieldCheck, FileClock } from 'lucide-react';
import { DocumentViewerDialog } from '../employees/DocumentViewerDialog';
import { DocDecisionPanel } from '../documents/DocDecisionPanel';
import { REG_STATUS } from '../firm/FirmApproval';
import { CompanyStatusPicker, isStoreStatus } from './CompanyStatusPicker';
import { StorePicker } from './StorePicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AY = 'aydinlatma_yukumlulugu';
const needsAy = (c) => c.aydinlatma_required || c.company_type === 'magaza' || !!c.document_files?.[AY];
const DOCS = [{ key: 'kvkk_taahhutnamesi', label: 'KVKK Taahhütnamesi' }, { key: AY, label: 'Aydınlatma Yükümlülüğü' }, { key: 'vergi_levhasi', label: 'Vergi Levhası' }, { key: 'ust_isveren_vekili', label: 'Üst İşveren Vekili' }];
const REQUIRED_DOCS = ['kvkk_taahhutnamesi', 'vergi_levhasi'];

// Self-servis firmada onay bekleyen yüklenmiş belge var mı
export const hasPendingFirmDocs = (company) => company.registered_via === 'self' && DOCS.some(d => company.document_files?.[d.key] && (company.documents?.[d.key] || 'beklemede') === 'beklemede');
// Kayıt belgelerinin tek tek durumu (Firmalar ekranı ipucu)
export const firmDocSummary = (company) => DOCS.map(d => ({
  label: d.label,
  text: company.document_files?.[d.key] ? (DOC_STATUS[company.documents?.[d.key]] || DOC_STATUS.beklemede).label : 'Yüklenmedi',
}));
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR') : '-');

const Info = ({ label, value, testId }) => (
  <div><p className="text-xs text-slate-400">{label}</p><p className="text-sm font-medium text-slate-800 break-words" data-testid={testId}>{value || '-'}</p></div>
);

export const RegistrationStatusBadge = ({ company, onClick }) => {
  const docsOnly = company.registration_status === 'approved' && hasPendingFirmDocs(company);
  const st = docsOnly ? { label: 'Belge Onayı Bekliyor', icon: FileClock, cls: 'bg-amber-50 border-amber-200 text-amber-800' } : REG_STATUS[company.registration_status];
  if (!st || (company.registration_status === 'approved' && !docsOnly)) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="outline" onClick={onClick} className={`cursor-pointer border ${st.cls} hover:opacity-80 animate-pulse`} data-testid={`registration-badge-${company.id}`}>
          <st.icon className="h-3.5 w-3.5 mr-1" />
          <span className="text-xs font-semibold whitespace-nowrap">{company.registration_status === 'rejected' ? 'Reddedildi' : 'İncele / Onayla'}</span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="right" className="bg-slate-800 text-white">
        <p>{docsOnly ? st.label : `Kayıt başvurusu: ${st.label}`}</p>
        <p className="text-xs text-slate-300">Detay için tıklayın</p>
      </TooltipContent>
    </Tooltip>
  );
};

const DOC_STATUS = {
  onay: { label: 'Onaylandı', cls: 'bg-emerald-100 text-emerald-700' },
  eksik: { label: 'Reddedildi', cls: 'bg-rose-100 text-rose-700' },
  beklemede: { label: 'Onay Bekliyor', cls: 'bg-amber-100 text-amber-700' },
};

const DocDecisionRow = ({ doc, status, note, validity, hasFile, onView }) => {
  const st = DOC_STATUS[status] || DOC_STATUS.beklemede;
  return (
    <div className="py-3 border-b last:border-0 space-y-1" data-testid={`review-doc-row-${doc.key}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium flex-1 min-w-32">{doc.label}</span>
        {hasFile && <Badge className={st.cls} data-testid={`review-doc-status-${doc.key}`}>{st.label}</Badge>}
        {hasFile
          ? <Button size="sm" variant="outline" onClick={onView} data-testid={`review-view-${doc.key}`}><Eye className="h-3.5 w-3.5 mr-1" />{(status || 'beklemede') === 'beklemede' ? 'Görüntüle ve Onayla' : 'Görüntüle'}</Button>
          : <span className="text-xs text-rose-500">Yüklenmemiş</span>}
      </div>
      {status === 'onay' && validity && <p className="text-xs text-slate-500">Geçerlilik: {validity.split('-').reverse().join('.')}</p>}
      {status === 'eksik' && note && <p className="text-xs text-rose-600" data-testid={`review-doc-note-${doc.key}`}>Red nedeni: {note}</p>}
    </div>
  );
};

export const RegistrationReviewDialog = ({ company: initialCompany, onClose, onDone, onRefresh, docKeys }) => {
  const [company, setCompany] = useState(initialCompany);
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [statusId, setStatusId] = useState(initialCompany?.company_status_id || '');
  const [statusError, setStatusError] = useState(false);
  const [docsError, setDocsError] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [storeError, setStoreError] = useState(false);
  if (!company) return null;
  const st = REG_STATUS[company.registration_status] || REG_STATUS.pending;
  const needsStatus = company.company_type !== 'magaza' && !!company.registration_status;
  const toStore = needsStatus && isStoreStatus(statuses.find(st => st.id === statusId));
  const allDocsApproved = [...REQUIRED_DOCS, ...(needsAy(company) ? [AY] : [])].every(k => company.documents?.[k] === 'onay');
  const isApproved = company.registration_status === 'approved' || !company.registration_status;

  const decide = async (action) => {
    if (action === 'reject' && !reason.trim()) { toast.error('Red sebebini yazın'); return; }
    if (action === 'approve' && ((needsStatus && !statusId) || (toStore && !storeId) || !allDocsApproved)) {
      setStatusError(needsStatus && !statusId);
      setStoreError(toStore && !storeId);
      setDocsError(!allDocsApproved);
      toast.error(!allDocsApproved ? 'Firmayı onaylamak için önce tüm belgeleri onaylayın' : !statusId ? 'Firmayı onaylamak için Firma Statüsü seçin' : 'Hangi mağaza olduğunu seçin');
      document.querySelector(`[data-testid="${needsStatus && !statusId ? 'review-company-status-select' : toStore && !storeId ? 'review-store-picker' : 'review-docs-box'}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/companies/${company.id}/registration`, { action, reason: reason.trim(), company_status_id: statusId, store_id: toStore ? storeId : '' });
      toast.success(action === 'approve' ? (toStore ? 'Onaylandı; kayıt seçilen mağazaya taşındı (Mağazalar menüsü)' : 'Firma onaylandı') : 'Firma kaydı reddedildi');
      onDone();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'İşlem başarısız');
    } finally { setSaving(false); }
  };

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="registration-review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">{company.company_type === 'magaza' ? 'Mağaza KVKK Belgeleri' : company.registration_status ? 'Firma Kayıt Başvurusu' : 'Firma Kayıt Belgeleri'} {company.registration_status && company.company_type !== 'magaza' && <Badge className={`border ${st.cls}`}>{st.label}</Badge>}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4 rounded-lg border p-4">
              <div className="sm:col-span-2"><Info label="Ticari Unvan" value={company.trade_name} testId="review-trade-name" /></div>
              <Info label="Firma Adı" value={company.name} />
              <Info label="SGK Sicil No" value={company.sgk_sicil_no} />
              <Info label="NACE Kodu" value={company.nace_kodu} />
              <div className="sm:col-span-2"><Info label="Faaliyet / İş Tanımı" value={company.activity_description} testId="review-activity" /></div>
              <Info label="Kayıt Tarihi" value={fmt(company.registered_at)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 rounded-lg border p-4">
              <Info label="Yetkili" value={`${company.contact_name || ''} ${company.contact_surname || ''}`} />
              <Info label="T.C. Kimlik No" value={company.contact_tc_kimlik_no} />
              <Info label="Telefon" value={company.contact_phone} />
              <Info label="E-posta" value={company.contact_email} />
            </div>
            <div className={`rounded-lg px-4 pt-3 pb-1 ${docsError && !allDocsApproved ? 'border-2 border-rose-500 ring-2 ring-rose-200' : 'border'}`} data-testid="review-docs-box">
              <p className="text-xs text-slate-400">Belgeler — onay/red için her belgeyi açıp inceleyin</p>
              {DOCS.filter(d => (!docKeys || docKeys.includes(d.key)) && (d.key !== AY || needsAy(company))).map(d => (
                <DocDecisionRow
                  key={d.key}
                  doc={d}
                  status={company.documents?.[d.key]}
                  note={company.documents?.[`${d.key}_note`]}
                  validity={company.documents?.[`${d.key}_validity`]}
                  hasFile={!!company.document_files?.[d.key]}
                  onView={() => setViewer(d)}
                />
              ))}
            </div>
            {needsStatus && (
              <CompanyStatusPicker value={statusId} error={statusError} disabled={isApproved} onItems={setStatuses}
                onChange={(v) => { setStatusId(v); setStatusError(false); }}>
                {toStore && !isApproved && <StorePicker value={storeId} error={storeError} onChange={(v) => { setStoreId(v); setStoreError(false); }} />}
              </CompanyStatusPicker>
            )}
            {company.consent?.accepted && (
              <p className="text-xs text-emerald-700 flex items-center gap-1"><ShieldCheck className="h-4 w-4" />Aydınlatma Metni onaylandı{company.consent.version ? ` (v${company.consent.version})` : ''}: {fmt(company.consent.accepted_at)}</p>
            )}
            {company.registration_reviewed_by && (
              <p className="text-xs text-slate-500">Son işlem: {company.registration_reviewed_by} · {fmt(company.registration_reviewed_at)}{company.registration_reason ? ` · Sebep: ${company.registration_reason}` : ''}</p>
            )}
            {rejecting && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">Red sebebi</p>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Firmaya iletilecek red sebebi..." rows={3} data-testid="review-reject-reason" />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t">
              {rejecting ? (
                <>
                  <Button variant="ghost" onClick={() => setRejecting(false)}>Vazgeç</Button>
                  <Button onClick={() => decide('reject')} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="review-reject-confirm"><XCircle className="h-4 w-4 mr-1" />Reddet</Button>
                </>
              ) : isApproved ? (
                <Button variant="ghost" onClick={onClose} data-testid="review-close-btn">Kapat</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setRejecting(true)} className="text-rose-600 border-rose-200" data-testid="review-reject-btn"><XCircle className="h-4 w-4 mr-1" />Reddet</Button>
                  {!allDocsApproved && <p className={`text-xs self-center mr-auto ${docsError ? 'font-semibold text-rose-600' : 'text-amber-700'}`} data-testid="review-docs-pending-hint">Firmayı onaylamak için önce tüm belgeleri onaylayın</p>}
                  <Button onClick={() => decide('approve')} disabled={saving || company.registration_status === 'approved'} className="bg-emerald-600 hover:bg-emerald-700" data-testid="review-approve-btn"><CheckCircle2 className="h-4 w-4 mr-1" />Onayla</Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <DocumentViewerDialog
        open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}
        entity={company} entityType="companies" subtitle={company.name}
        docKey={viewer?.key} docLabel={viewer?.label} mode="latest"
        onReplaced={(info) => { setCompany(c => ({ ...c, documents: { ...(c.documents || {}), [viewer.key]: 'beklemede' }, document_files: { ...(c.document_files || {}), [viewer.key]: info } })); onRefresh?.(); }}
        decisionPanel={viewer && (
          <DocDecisionPanel
            key={viewer.key}
            url={`${API}/companies/${company.id}/documents/${viewer.key}/decision`}
            docLabel={viewer.label}
            status={company.documents?.[viewer.key]}
            validity={company.documents?.[`${viewer.key}_validity`]}
            onDecided={(res) => {
              const key = viewer.key;
              setCompany(prev => ({ ...prev, documents: { ...(prev.documents || {}), [key]: res.status, [`${key}_note`]: res.note, ...(res.status === 'onay' ? { [`${key}_validity`]: res.validity } : {}) } }));
              setViewer(null);
              onRefresh?.();
            }}
          />
        )}
      />
    </>
  );
};
